import { RECORD_MAPPING_LIVE_SOURCE } from '../constants/partner-data-gateway.constant';
import {
  IngestionDetail,
  JsonObject,
  RecordMappingLive,
} from '../domain/types';
import { formatWireLabel } from './format.utils';
import { isTerminalStatus } from './ingestion-status.utils';

/**
 * Whether the mapping queue could not be read. PDGS still returns every live
 * count as `'0'` in that case, so the counts have to be suppressed rather
 * than rendered: a zero that means "we could not look" reads identically to
 * a zero that means "nothing failed".
 *
 * Anything other than a successful queue read counts as unavailable, so a
 * source PDGS adds later fails closed instead of silently showing zeroes.
 */
export const isLiveQueueUnavailable = (live: RecordMappingLive): boolean =>
  live.source !== RECORD_MAPPING_LIVE_SOURCE.QueueQuery;

/**
 * The counts to show for one ingestion, and where they came from.
 *
 * A discriminated union on `isUnavailable` rather than four nullable
 * fields: when the queue could not be read there is no number to show, and
 * when it could, every count is present. Callers get that from the type
 * instead of writing a fallback that can never run.
 *
 * `pending` stays nullable on the available branch because a finished
 * ingestion has no queue left to drain.
 */
export type RecordMappingSelection =
  | {
      /** True when these are terminal aggregates rather than a live sample. */
      isFinal: boolean;
      isUnavailable: true;
      mapped: null;
      failed: null;
      dropped: null;
      pending: null;
    }
  | {
      isFinal: boolean;
      isUnavailable: false;
      mapped: string;
      failed: string;
      dropped: string;
      pending: string | null;
    };

/**
 * Picks the authoritative half of `recordMapping`.
 *
 * Once the ingestion reaches a terminal status the finalized aggregate is
 * the answer and the queue is irrelevant — including when the queue read
 * failed, which is why the availability check happens only on the live path.
 */
export const selectRecordMappingCounts = (
  ingestion: IngestionDetail,
): RecordMappingSelection => {
  const { finalized, live } = ingestion.recordMapping;

  if (isTerminalStatus(ingestion.status)) {
    return {
      isFinal: true,
      isUnavailable: false,
      mapped: finalized.mapped,
      failed: finalized.failed,
      dropped: finalized.dropped,
      pending: null,
    };
  }

  if (isLiveQueueUnavailable(live)) {
    return {
      isFinal: false,
      isUnavailable: true,
      mapped: null,
      failed: null,
      dropped: null,
      pending: null,
    };
  }

  return {
    isFinal: false,
    isUnavailable: false,
    mapped: live.mapped,
    failed: live.failed,
    dropped: live.dropped,
    pending: live.pending,
  };
};

/** One row of the failure-reason breakdown. */
export interface FailureReasonRow {
  /** The PDGS reason, verbatim — the stable key for React and for tests. */
  reason: string;
  /** The reason rendered for display. */
  label: string;
  /** BIGINT string, never coerced to a number for display. */
  count: string;
}

/** Counts are BIGINT strings; `Number` would lose precision on the compare. */
const toCount = (value: unknown): bigint | null => {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    return BigInt(value);
  } catch {
    return null;
  }
};

/**
 * Turns the opaque `byFailureReason` map into display rows, largest first.
 *
 * The map is keyed by a PDGS enum this module deliberately does not mirror,
 * so labels are derived rather than looked up and an unknown reason still
 * renders. Ties break on the reason name so the order does not reshuffle
 * between polls.
 */
export const toFailureReasonRows = (
  byFailureReason: JsonObject,
): FailureReasonRow[] =>
  Object.entries(byFailureReason)
    .map(([reason, value]) => ({ reason, count: toCount(value) }))
    .filter(
      (entry): entry is { reason: string; count: bigint } =>
        entry.count !== null && entry.count > BigInt(0),
    )
    .sort(
      (a, b) => Number(b.count - a.count) || a.reason.localeCompare(b.reason),
    )
    .map(({ reason, count }) => ({
      reason,
      label: formatWireLabel(reason),
      count: count.toString(),
    }));
